import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { storePostLoginRedirect, consumePostLoginRedirect } from '../utils/postLoginRedirect';

interface AuthRouteGuardProps {
  children: React.ReactNode;
}

const AuthRouteGuard: React.FC<AuthRouteGuardProps> = ({ children }) => {
  const [user] = useAuthState(auth);
  const location = useLocation();
  const navigate = useNavigate();

  // After login (multi-step verification included), return to the page that
  // originally bounced to /login — e.g. an email deep link into a conversation.
  useEffect(() => {
    if (!user) return;
    const stored = consumePostLoginRedirect();
    if (stored && stored !== location.pathname + location.search) {
      navigate(stored, { replace: true });
    }
  }, [user, navigate, location.pathname, location.search]);

  // Allow access to auth routes without authentication
  const authPaths = ['/login', '/new-signup', '/signup-application', '/instagram-verification', '/returning-user', '/verification', '/email-sent'];
  if (authPaths.includes(location.pathname)) {
    return <>{children}</>;
  }

  // For explore routes, allow access without authentication
  const clientPathRegex = /^\/explore\/client\/[^/]+\/?$/;
  if (clientPathRegex.test(location.pathname)) {
    return <>{children}</>;
  }

  // For all other routes, require authentication
  if (!user) {
    storePostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default AuthRouteGuard;
